// /frontend/components/newfolder/FCADashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Paper, Typography, TextField, Button, Divider } from '@mui/material';
import PageContainer from '../common/PageContainer';
import axios from 'axios';
import FCAForm from './FCAForm';
import CostCentersAdminDialog from './CostCentersAdminDialog';
import SettingsIcon from '@mui/icons-material/Settings';
import FCATable from './FCATable';
import FCADirectsTable from './FCADirectsTable';
import EmptyState from '../common/EmptyState';
import RefreshButton from '../common/RefreshButton';
import { formatNumber, formatPercent } from '../../utils/format';

export default function FCADashboard() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0,7));
  // 🔴 R16 N1: `null` heisst „noch nie geladen", `[]` hiesse „geladen und
  // leer". Mit `[]` laese der erste Neulauf eines Monats ohne FCA-Zeilen als
  // Erstaufbau — der Knopf sperrte, statt zu drehen.
  const [rows, setRows] = useState(null);
  // 🔴 Der Wert wurde bis zum 2026-10-07 verworfen (`const [, setLoading]`).
  // Ohne ihn konnte der Auffrisch-Knopf nicht sagen, dass ein Abruf laeuft —
  // und ein zweiter Klick startete einen zweiten. Er wird jetzt gelesen.
  const [loading, setLoading] = useState(false);
  const [openCenters, setOpenCenters] = useState(false);
  const [directRows, setDirectRows] = useState([]);
  // 🔴 Der Fehlerzustand fehlte. `Promise.all` ohne `catch` liess `rows` und
  //   `directRows` auf ihren Vorgabewerten stehen, und die KPI-Kacheln rechneten
  //   daraus 0 € aus — eine Zahl, die „unbekannt" bedeutet, als „null"
  //   ausgegeben (Regel 22: „Unbekannt ist nie 0"). Lokal warf CRA ein Overlay,
  //   in Prod war die Seite einfach leer und sah aus wie „dieser Monat hat keine
  //   Daten". Der Unterschied zwischen den beiden ist der ganze Punkt.
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    // 🔴 `try/finally`, seit `loading` GELESEN wird. Vorher war der Wert
    // verworfen, ein gescheiterter Abruf also folgenlos; jetzt liesse ein 500
    // den Knopf dauerhaft drehen und dauerhaft gesperrt stehen.
    try {
      // 🔴 EIN Abruf, EIN Urteil: beide Listen gehoeren zur selben Auswertung
      //   (Monat). Nacheinander geladen hiesse, dass ein Fehler im zweiten die
      //   erste schon gesetzt hat — die Seite zeigte dann halbe Zahlen als ganze.
      const [r, d] = await Promise.all([
        axios.get('/api/fca/by-sku', { params: { month }}),
        axios.get('/api/fca/fulfillment/by-sku', { params: { month }}),
      ]);
      setRows(r.data || []);
      setDirectRows(d.data || []);
      // 🔴 Erst der ERFOLG raeumt den Fehler weg — nicht schon der Start des
      //   Versuchs. Sonst risse der „Try again"-Klick die Fehlerkarte sofort ab
      //   und legte fuer die Dauer des Abrufs wieder die Kacheln frei, die aus
      //   `rows === null` fuenf Nullen rechnen: genau die Zahl, gegen die diese
      //   Karte antritt (Regel 22: „Unbekannt ist nie 0").
      setError(null);
    } catch (e) {
      // 🔴 KEIN `setRows([])` und kein `setDirectRows([])`.
      //   `[]` hiesse „geladen und leer" und zerstoerte genau das Signal, das
      //   `rows === null` traegt: nach einem gescheiterten ERSTEN Lauf haette die
      //   Seite nie Daten gezeigt und gaelte trotzdem als geladen. Der
      //   Auffrisch-Knopf drehte dann fuer eine Flaeche, die noch nie etwas
      //   angezeigt hat — und der naechste Leser koennte „nie geladen" nicht mehr
      //   von „diesen Monat gibt es nichts" unterscheiden.
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- load is stable after mount
  useEffect(()=>{ load(); }, [month]);

  const kpis = useMemo(()=>{
    const sum = (f) => (rows ?? []).reduce((a,b)=>a + Number(b[f] || 0), 0);
    const revenue = sum('revenue_net');
    const total_cost = sum('total_cost_net');
    const margin = revenue - total_cost;
    const margin_pct = revenue ? (margin / revenue) : 0;
    const overhead = sum('overhead_net');
    return { revenue, total_cost, margin, margin_pct, overhead };
  }, [rows]);

  return (
    // 🔴 G0b.5 (2026-10-13): nackter `Container` → `PageContainer width="wide"`.
    // Vorher wirkungslos gedeckelt (Override zog `xl` per `!important` auf 1440),
    // jetzt 1600 — die benannte sichtbare Aenderung `/fca`: 1440 → 1600
    // (`inhaltsbreite.md` §5.3:721, Plan §3.3).
    <PageContainer width="wide" sx={{ py:2 }}>
      <Typography variant="h5" sx={{ mb:2 }}>Full Cost Accounting</Typography>
      <Paper sx={{ p:2, mb:2 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            label="Month (YYYY-MM)"
            value={month}
            onChange={(e)=>setMonth(e.target.value)}
            size="small"
          />
          <Button startIcon={<SettingsIcon />} onClick={()=>setOpenCenters(true)}>
             Manage cost centers
           </Button>
          {/* 🔴 W13 (2026-10-07): der Refresh stand als `outlined`-Button
              zwischen den Bedienelementen. Ein Refresh ist nie primaer und
              traegt keine Fuellung — er ist jetzt der eine Auffrisch-Knopf (E8).
              ⚠️ `firstLoad`: diese Seite hat GAR KEINEN Aufbauzustand; die
              Kacheln stehen im ersten Abruf auf 0. Der Knopf dreht sich dort
              nicht mehr mit — der fehlende Aufbauzustand ist als Befund
              abgelegt (`docs/backlog-auto-repair/2026-10-07-aufbauzustand-ist-ein-spinner.md`),
              nicht mit einem zweiten Spinner zugedeckt.
              🔴 R16 N1: der Diskriminator ist `rows === null` („noch nie
              geladen"), nicht `rows.length === 0`.
              🔴 Und `&& !error` gehoert dazu (R16 N1 / R15 W-A, 2026-10-07):
              nach einem gescheiterten ERSTlauf bleibt `rows === null`, also
              laese der Wiederholversuch ohne diesen Ausschluss als Erstaufbau
              — der Knopf sperrte still, statt zu drehen, und der Spinner ist
              hier der EINZIGE Fortschrittshinweis. Mit dem Ausschluss dreht
              der ERSTE Aufbau weiter still und jede Wiederholung dreht. */}
          <RefreshButton
            ariaLabel="Refresh full cost accounting"
            loading={loading}
            firstLoad={loading && rows === null && !error}
            onClick={load}
          />
        </Box>
        <Divider sx={{ my:2 }}/>
        {error ? (
          // 🔴 Kein Zahlenblock im Fehlerfall. Die Kacheln rechnen aus `rows`,
          //   und im Fehlerfall gibt es keine Zeilen — dort stuenden fünf
          //   Nullen, und „0 € Umsatz" ist eine andere Aussage als „unbekannt".
          <EmptyState
            reason="error"
            message="Could not load the full-cost figures."
            detail="The figures are unknown for this month."
            action={(
              // 🔴 `disabled={loading}` (R15 W-A Punkt 2, 2026-10-07): ohne den
              //   Riegel startet ein Doppelklick ZWEI `Promise.all`. Antwortet
              //   die aeltere Anfrage zuletzt und scheitert, ueberschreibt ihr
              //   `setError(e)` einen frischen Erfolg — die Karte stuende dann
              //   ueber vorhandenen Zahlen. Der Auffrisch-Knopf sperrt sich
              //   waehrend `loading` schon selbst; das ist der zweite Weg.
              <Button variant="outlined" size="small" onClick={load} disabled={loading}>
                Try again
              </Button>
            )}
          />
        ) : (
          <Grid container spacing={2}>
            <KPI label="Revenue" value={kpis.revenue}/>
            <KPI label="Total Cost" value={kpis.total_cost}/>
            <KPI label="Overhead" value={kpis.overhead}/>
            <KPI label="Margin" value={kpis.margin}/>
            <KPI label="Margin %" value={formatPercent(kpis.margin_pct, { digits: 1 })} />
          </Grid>
        )}
      </Paper>

      <Grid container spacing={2}>
        {/* Ohne Zahlen keine Tabelle: `FCATable` hat keinen Leerzustand und
            zeichnete im Fehlerfall eine Kopfzeile ueber null Zeilen — genau das
            „sieht aus wie nichts da", gegen das `EmptyState` antritt. */}
        <Grid size={{ xs: 12, md: error ? 12 : 6 }}>
          <FCAForm month={month}/>
        </Grid>
        {!error && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              {/* `?? []` ist nicht Kosmetik: `FCATable`s Vorgabewert greift nur
                  bei `undefined`, nicht bei `null` — und `null` heisst hier
                  „noch nie geladen". */}
              <FCATable rows={rows ?? []}/>
            </Grid>
            <Grid size={12}>
                <Paper sx={{ p:2, mb:1 }}>
                <Typography variant="subtitle1">Fulfillment Data (Directs only) — {month}</Typography>
                <Typography variant="body2" color="text.secondary">
                    Auto-aggregated from shipments and storage snapshots; no manual input needed.
                </Typography>
                </Paper>
                <FCADirectsTable rows={directRows}/>
            </Grid>
          </>
        )}
      </Grid>

      <CostCentersAdminDialog open={openCenters} onClose={()=>setOpenCenters(false)} />
    </PageContainer>
  );
}

function KPI({ label, value }) {
  const fmt = (v) => (typeof v === 'number') ? formatNumber(v) : v;
  return (
    <Grid size={{ xs: 6, md: 2.4 }}>
      <Paper variant="outlined" sx={{ p:2 }}>
        <Typography variant="caption">{label}</Typography>
        <Typography variant="h6">{fmt(value)}</Typography>
      </Paper>
    </Grid>
  );
}
